/**
 * Singleton WebSocket for the team messaging stream.
 * Never tears down while the app is running — stays connected
 * across page navigations. Reconnects with exponential backoff.
 * Refetches channels on reconnect to sync missed messages.
 */

import { useEffect, useState } from 'react'
import { teamWsUrl, listChannels, type TeamWsEvent } from '@/api/team'
import { useAuthStore } from '@/store/auth'
import { useTeamStore } from '@/store/team'
import { notify } from '@/lib/notify'

const SOUND_URL = '/notification.wav'

type Listener = (ev: TeamWsEvent) => void

interface TeamConnection {
  ws: WebSocket | null
  listeners: Set<Listener>
  reconnectTimer: ReturnType<typeof setTimeout> | null
  backoffMs: number
  closed: boolean
  url: string
  connected: boolean
}

let connection: TeamConnection | null = null

function open(conn: TeamConnection) {
  if (conn.closed) return
  const ws = new WebSocket(conn.url)
  conn.ws = ws

  ws.onopen = () => {
    conn.backoffMs = 1000
    conn.connected = true
    console.log('[team-ws] connected')

    // On (re)connect, refresh channel list to pick up messages
    // that arrived while disconnected.
    listChannels()
      .then((res) => useTeamStore.getState().setChannels(res.channels))
      .catch(() => {})
  }

  ws.onmessage = (ev) => {
    try {
      const data = JSON.parse(ev.data) as TeamWsEvent
      console.log('[team-ws] event:', data.type)
      conn.listeners.forEach((l) => l(data))
    } catch {
      // ignore malformed frames
    }
  }

  ws.onclose = (ev) => {
    conn.ws = null
    conn.connected = false
    console.log('[team-ws] closed', ev.code, ev.reason)
    if (conn.closed) return
    conn.reconnectTimer = setTimeout(() => open(conn), conn.backoffMs)
    conn.backoffMs = Math.min(conn.backoffMs * 2, 30_000)
  }

  ws.onerror = () => {
    console.log('[team-ws] error — will reconnect')
    ws.close()
  }
}

function teardown(conn: TeamConnection) {
  conn.closed = true
  if (conn.reconnectTimer) clearTimeout(conn.reconnectTimer)
  conn.ws?.close()
  if (connection === conn) connection = null
}

function ensureConnection(): TeamConnection {
  const url = teamWsUrl()
  if (connection && connection.url === url && !connection.closed) {
    return connection
  }
  if (connection) teardown(connection)

  const conn: TeamConnection = {
    ws: null,
    listeners: new Set(),
    reconnectTimer: null,
    backoffMs: 1000,
    closed: false,
    url,
    connected: false,
  }
  connection = conn
  open(conn)
  return conn
}

export function sendTeamFrame(data: unknown) {
  if (connection?.ws?.readyState === WebSocket.OPEN) {
    connection.ws.send(JSON.stringify(data))
  }
}

// Module-level audio — shared across hook instances, survives re-renders
let notifAudio: HTMLAudioElement | null = null
function playNotifSound() {
  try {
    if (!notifAudio) {
      notifAudio = new Audio(SOUND_URL)
      notifAudio.volume = 0.4
    }
    notifAudio.currentTime = 0
    notifAudio.play().catch(() => {})
  } catch {
    // Browser may block; ignore
  }
}

/**
 * Connect to the team WebSocket and dispatch events into the Zustand store.
 * The connection is NEVER torn down on unmount — it persists for the
 * lifetime of the app so messages aren't lost during page navigation.
 */
export function useTeamStream(): { connected: boolean } {
  const token = useAuthStore((s) => s.token)
  const [connected, setConnected] = useState(false)

  useEffect(() => {
    if (!token) return
    const conn = ensureConnection()

    const listener: Listener = (ev) => {
      const store = useTeamStore.getState()
      const currentUserId = useAuthStore.getState().user?.id

      switch (ev.type) {
        case 'Message':
          store.appendMessage(ev.message)
          // Sound + toast when message is from someone else AND either:
          // - panel is closed, OR
          // - panel is open but viewing a different channel
          if (ev.message.sender_id !== currentUserId) {
            const isViewingThisChannel =
              store.panelOpen && store.activeChannelId === ev.message.channel_id
            if (!isViewingThisChannel) {
              const senderName = ev.message.sender_name || ev.message.sender_email
              notify.info(`Team · ${senderName}`, {
                description: ev.message.body.slice(0, 80),
                source: 'team',
              })
              playNotifSound()
            }
          }
          break
        case 'Typing':
          store.setTyping(ev.channel_id, ev.user_id, ev.is_typing)
          break
        case 'PresenceChange':
          store.setUserOnline(ev.user_id, ev.online)
          break
        case 'ChannelCreated':
          store.upsertChannel(ev.channel)
          break
        case 'ReadReceipt':
          break
      }
    }

    conn.listeners.add(listener)

    const poll = setInterval(() => {
      setConnected(conn.connected)
    }, 2000)
    setConnected(conn.connected)

    return () => {
      clearInterval(poll)
      conn.listeners.delete(listener)
      // Do NOT teardown — keep the connection alive across page navigations.
      // Only teardown happens when token changes (ensureConnection detects
      // URL mismatch) or the tab is closed.
    }
  }, [token])

  return { connected }
}
